import React from "react"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Trophy, Star, TrendingUp, X } from "lucide-react"
import { GoogleLogin } from "./google-login"
import { useTranslation } from "../contexts/translation-context"

interface SignupPromptProps {
  score: number
  totalQuestions: number
  onClose: () => void
  onSignupSuccess: () => void
  embedded?: boolean
}

export function SignupPrompt({ score, totalQuestions, onClose, onSignupSuccess, embedded = false }: SignupPromptProps) {
  const { t } = useTranslation()
  const [showLogin, setShowLogin] = useState(false)

  if (embedded) {
    // Embedded mode - just return the login component without modal wrapper
    return (
      <GoogleLogin
        onSuccess={() => {
          onSignupSuccess()
        }}
        showBenefits={false}
        title=""
        description=""
      />
    )
  }

  // Modal mode (existing behavior)
  if (showLogin) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute -top-2 -right-2 z-10 bg-white rounded-full shadow-md hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </Button>
          <GoogleLogin
            onSuccess={() => {
              onSignupSuccess()
              onClose()
            }}
            title={t("signup.greatJobSaveProgress")}
            description={t("signup.youScoredSignUp", { score: score.toString() })}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center relative">
          <Button variant="ghost" size="sm" onClick={onClose} className="absolute top-2 right-2">
            <X className="w-4 h-4" />
          </Button>
          <div className="mx-auto w-16 h-16 bg-secondary-50 rounded-full flex items-center justify-center mb-4">
            <Trophy className="w-8 h-8 text-secondary" />
          </div>
          <CardTitle className="text-2xl text-primary">{t("signup.awesomeWork")}</CardTitle>
          <CardDescription>
            {t("signup.scoredOutOf", {
              score: score.toString(),
              total: (totalQuestions * 10).toString(),
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-secondary mb-2">
              {score} {t("quiz.points")}
            </div>
            <div className="text-sm text-muted-foreground">
              {Math.round((score / (totalQuestions * 10)) * 100)}% {t("signup.accuracy")}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-center">{t("signup.signUpNowToUnlock")}</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-secondary" />
                <span>{t("signup.benefits.saveScoresAndTrack")}</span>
              </div>
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-primary" />
                <span>{t("signup.benefits.accessAllPastExams")}</span>
              </div>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-secondary" />
                <span>{t("signup.benefits.detailedAnalytics")}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => setShowLogin(true)}
              className="w-full bg-secondary hover:bg-secondary-600 text-white"
              size="lg"
            >
              {t("signup.signUpAndSaveProgress")}
            </Button>
            <Button onClick={onClose} variant="outline" className="w-full">
              {t("signup.maybeLater")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
